import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import InputComponent from "../../components/Input";
import { onTrackOrder } from "../../store/actions/user-action";
import { MdBorderColor } from "react-icons/md";
import RefundModal from "../../components/Modal/RefundModal";
import OrderDetailsCard from "../../components/TrackOrderComponents/OrderDetailsCard";
import DriverDetailsCard from "../../components/TrackOrderComponents/DriverDetailsCard";
import VendorDetailsCard from "../../components/TrackOrderComponents/VendorDetailsCard";
import { formatDate } from "../../utils";
import { MdPayments } from "react-icons/md";
import { GiCardPickup } from "react-icons/gi";
import { FaRegIdCard } from "react-icons/fa6";
import { FaCheckDouble } from "react-icons/fa6";
import { CgProfile } from "react-icons/cg";
import { IoPhonePortrait } from "react-icons/io5";
import { MdMarkEmailRead } from "react-icons/md";
import { FaMoneyBill1Wave } from "react-icons/fa6";
import { FaIdBadge } from "react-icons/fa";
import { FaCartArrowDown } from "react-icons/fa6";
import { BiSolidDiscount } from "react-icons/bi";
import { TbTruckDelivery } from "react-icons/tb";
import { GiCementShoes } from "react-icons/gi";
import { GrMoney } from "react-icons/gr";
import { FaHome } from "react-icons/fa";
import { FaBuilding } from "react-icons/fa6";
import UserImage from "../../../src/assets/img/userPNG.png";
import RefundDetailsCard from "../../components/TrackOrderComponents/RefundDetailsCard";
import { useLocation } from "react-router-dom";

const TrackOrderPage = () => {
  const dispatch = useDispatch();
  const [input, setInput] = useState("");
  const [activeTab, setActiveTab] = useState("Order Details");
  const [isLoading, setIsLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const { orderDetails } = useSelector((state) => state.orderDetails);
  const location = useLocation();

  const paymentBreakdown = useMemo(() => {
    const { cartValue = 0, couponDiscount = 0, deliveryFee = 0, gst = 0, platformFee = 0, walletDiscount = 0 } = orderDetails.paymentBreakdown || {};
    const finalAmount = cartValue + deliveryFee + gst + platformFee - couponDiscount - walletDiscount;
    return { cartValue, couponDiscount, deliveryFee, gst, platformFee, walletDiscount, finalAmount };
  }, [orderDetails.paymentBreakdown]);

  const getStatusColor = useCallback((status) => {
    const statusColors = {
      Delivered: "bg-green-100 text-green-800 border-green-200",
      Processing: "bg-blue-100 text-blue-800 border-blue-200",
      Pending: "bg-yellow-100 text-yellow-800 border-yellow-200",
    };
    return statusColors[status] || "bg-gray-100 text-gray-800 border-gray-200";
  }, []);

  const tabs = useMemo(() => [
    { id: "Order Details", icon: "📦" },
    { id: "Customer Details", icon: "👤" },
    { id: "Driver Details", icon: "🚗" },
    { id: "Vendor Details", icon: "🏪" },
    { id: "Payment Details", icon: "💳" },
    { id: "Refund Details", icon: "💰" },
  ], []);

  const quickStatsData = useMemo(() => [
    { label: "Cart", value: `₹${paymentBreakdown.cartValue}`, icon: <FaCartArrowDown className="text-blue-500" /> },
    { label: "Discount", value: `₹${paymentBreakdown.couponDiscount}`, icon: <BiSolidDiscount className="text-green-500" /> },
    { label: "Wallet Discount", value: `₹${paymentBreakdown.walletDiscount}`, icon: <FaMoneyBill1Wave className="text-purple-500" /> },
    { label: "Delivery", value: `₹${paymentBreakdown.deliveryFee}`, icon: <TbTruckDelivery className="text-orange-500" /> },
    { label: "GST", value: `₹${paymentBreakdown.gst}`, icon: <GrMoney className="text-purple-500" /> },
    { label: "Platform Fee", value: `₹${paymentBreakdown.platformFee}`, icon: <GiCementShoes className="text-red-500" /> },
    { label: "Final Amount", value: `₹${paymentBreakdown.finalAmount}`, icon: <FaMoneyBill1Wave className="text-green-600" /> },
  ], [paymentBreakdown]);

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  const handleChange = useCallback((name, value) => {
    if (name === "input") {
      setInput(value);
    }
  }, []);

  const handleTabChange = useCallback((tabId) => {
    setActiveTab(tabId);
  }, []);

  useEffect(() => {
    if (location?.search) {
      const urlInput = location.search.split("=")[1];
      if (urlInput) setInput(urlInput);
    }
  }, [location]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (input) {
        setIsLoading(true);
        dispatch(onTrackOrder({ id: input })).finally(() => setIsLoading(false));
      }
    }, 1000);

    return () => clearTimeout(timer);
  }, [input, dispatch]);

  const hasOrderDetails = Object.keys(orderDetails).length > 0;

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header Section */}
      <div className="bg-white rounded-2xl p-4 sm:p-6 shadow-lg border border-gray-200">
        <div className="flex justify-between space-y-4">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-gray-800 mb-2">Track Order</h1>
            <p className="text-sm sm:text-base text-gray-600">Enter order ID to track order details</p>
          </div>
          <div className="">
            <InputComponent
              icon={<MdBorderColor className="text-primaryRed" />}
              value={input}
              type="text"
              name="input"
              placeholder="Enter order ID"
              onChange={(e) => handleChange("input", e.target.value)}
              className="border-2 border-gray-200 focus:border-primaryRed transition-colors duration-200"
            />
          </div>
        </div>
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100">
          <div className="flex items-center justify-center space-x-3">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primaryRed"></div>
            <span className="text-gray-600">Searching for order...</span>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-xl border border-white/20 overflow-hidden">
        {hasOrderDetails ? (
          <>
            {/* Enhanced Tabs */}
            <div className="border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white">
              <div className="flex overflow-x-auto scrollbar-hide px-2 sm:px-0">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    className={`flex items-center space-x-1 sm:space-x-2 px-3 sm:px-6 py-3 sm:py-4 text-xs sm:text-sm font-medium whitespace-nowrap transition-all duration-200 ${
                      activeTab === tab.id
                        ? "text-primaryRed border-b-2 border-primaryRed bg-white shadow-sm"
                        : "text-gray-600 hover:text-gray-800 hover:bg-gray-50"
                    }`}
                    onClick={() => handleTabChange(tab.id)}
                  >
                    <span className="text-sm sm:text-lg">{tab.icon}</span>
                    <span className="hidden sm:inline">{tab.id}</span>
                    <span className="sm:hidden">{tab.id.split(' ')[0]}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Quick Stats - Only show on Order Details tab */}
            {activeTab === "Order Details" && (
              <div className="p-4 sm:p-6 bg-gradient-to-r from-blue-50 to-indigo-50 border-b border-gray-100">
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-3 sm:gap-4">
                  {quickStatsData.map((item, index) => (
                    <div key={index} className="bg-white rounded-xl p-3 sm:p-4 shadow-sm hover:shadow-md transition-shadow duration-200">
                      <div className="flex items-center space-x-2 sm:space-x-3">
                        <div className="p-1.5 sm:p-2 bg-gray-50 rounded-lg flex-shrink-0">
                          {item.icon}
                        </div>
                        <div className="min-w-0">
                          <p className="text-xs text-gray-500 font-medium truncate">{item.label}</p>
                          <p className="text-sm font-bold text-gray-800 truncate">{item.value}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Tab Content */}
            <div className="p-4 sm:p-6">
              {activeTab === "Order Details" && orderDetails && (
                <div className="space-y-4 sm:space-y-6">
                  <div className="bg-gradient-to-r from-gray-50 to-blue-50 rounded-xl p-4 sm:p-6">
                    <h3 className="text-lg sm:text-xl font-bold text-gray-800 mb-4 flex items-center space-x-2">
                      <span>📋</span>
                      <span>Order Status</span>
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                      {[
                        { label: "Status", value: orderDetails.status, icon: <FaCheckDouble />, isStatus: true },
                        { label: "Order ID", value: orderDetails.order_id, icon: <FaRegIdCard /> },
                        { label: "Pickup Date", value: orderDetails.pickup_date && formatDate(orderDetails.pickup_date), icon: <GiCardPickup /> },
                        { label: "Pickup Time", value: orderDetails?.pickup_time, icon: <GiCardPickup /> },
                        { label: "No of Pairs", value: orderDetails?.order_items?.length, icon: <GiCardPickup /> },
                        { label: "Payment Status", value: orderDetails.payment_status, icon: <MdPayments />, isStatus: true },
                        { label: "Address Type", value: orderDetails?.address?.address_type, icon: <FaBuilding /> },
                        { label: "Latitude", value: orderDetails?.address?.location?.lat?.toFixed(2), icon: <GiCardPickup /> },
                        { label: "Longitude", value: orderDetails?.address?.location?.lng?.toFixed(2), icon: <MdPayments /> },
                      ].map((item, index) => (
                        <div key={index} className="bg-white rounded-xl p-4 shadow-sm hover:shadow-md transition-all duration-200 h-full">
                          <div className="flex items-start space-x-3 h-full">
                            <div className="p-2 bg-gray-50 rounded-lg text-gray-500 flex-shrink-0">
                              {item.icon}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-xs text-gray-500 font-medium mb-1">{item.label}</p>
                              {item.isStatus ? (
                                <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(item.value)}`}>
                                  {item.value}
                                </span>
                              ) : (
                                <p className="text-sm font-semibold text-gray-800 break-words">{item.value}</p>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                      <div className="md:col-span-3 bg-white rounded-xl p-4 shadow-sm">
                        <div className="flex items-start space-x-3">
                          <div className="p-2 bg-gray-50 rounded-lg text-gray-500">
                            <FaHome />
                          </div>
                          <div>
                            <p className="text-xs text-gray-500 font-medium mb-1">Address</p>
                            <p className="text-sm font-semibold text-gray-800">{orderDetails?.address?.full_address}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="space-y-2 sm:space-y-3">
                    <h3 className="text-lg sm:text-xl font-bold text-gray-800 flex items-center space-x-2">
                      <span>📦</span>
                      <span>Order Items ({orderDetails.order_items?.length})</span>
                    </h3>
                    {orderDetails.order_items?.map((item, index) => (
                      <OrderDetailsCard
                        key={index}
                        orderDetails={item}
                        index={index}
                        onBarcodeSaved={() => dispatch(onTrackOrder({ id: input }))}
                      />
                    ))}
                  </div>
                </div>
              )}

              {activeTab === "Customer Details" && orderDetails?.user && (
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-4 sm:p-6 space-y-6">
                  <h3 className="text-lg sm:text-xl font-bold text-gray-800 flex items-center space-x-2">
                    <span>👤</span>
                    <span>Customer Information</span>
                  </h3>
                  <div className="flex flex-col lg:flex-row gap-4 sm:gap-6">
                    <div className="flex-1 space-y-3 sm:space-y-4">
                      {[
                        { label: "Name", value: orderDetails.user.full_name, icon: <CgProfile /> },
                        { label: "Phone", value: orderDetails.user.phone, icon: <IoPhonePortrait /> },
                        { label: "Email", value: orderDetails.user.email, icon: <MdMarkEmailRead /> },
                      ].map((item, index) => (
                        <div key={index} className="bg-white rounded-xl p-3 sm:p-4 shadow-sm hover:shadow-md transition-shadow duration-200">
                          <div className="flex items-center space-x-3">
                            <div className="p-2 bg-gray-50 rounded-lg text-gray-500 flex-shrink-0">
                              {item.icon}
                            </div>
                            <div className="min-w-0">
                              <p className="text-xs text-gray-500 font-medium">{item.label}</p>
                              <p className="text-sm font-semibold text-gray-800 break-words">{item.value}</p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="w-full lg:w-1/3">
                      <div className="bg-white rounded-xl p-4 shadow-sm">
                        <img
                          src={orderDetails?.userUser?.avatar || UserImage}
                          alt="Customer"
                          className="w-full h-32 sm:h-48 object-cover rounded-lg hover:scale-105 transition-transform duration-200"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Referred Users */}
                  {orderDetails.user.referral_code_users?.length > 0 && (
                    <div>
                      <h4 className="text-base font-bold text-gray-800 mb-3 flex items-center space-x-2">
                        <span>🎁</span>
                        <span>Referred Users ({orderDetails.user.referral_code_users.length})</span>
                      </h4>
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                        {orderDetails.user.referral_code_users.map((referred, index) => (
                          <div key={index} className="bg-white rounded-xl p-3 sm:p-4 shadow-sm flex items-center space-x-3">
                            <div className="p-2 bg-indigo-50 rounded-lg text-indigo-500 flex-shrink-0">
                              <CgProfile />
                            </div>
                            <div className="min-w-0">
                              <p className="text-sm font-semibold text-gray-800 truncate">{referred.name || 'N/A'}</p>
                              <p className="text-xs text-gray-500">{referred.phone}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {activeTab === "Driver Details" && orderDetails?.driver && (
                <div className="transform hover:scale-[1.01] transition-transform duration-200">
                  <DriverDetailsCard driver={orderDetails.driver} />
                </div>
              )}

              {activeTab === "Vendor Details" && orderDetails?.vendor && (
                <div className="transform hover:scale-[1.01] transition-transform duration-200">
                  <VendorDetailsCard vendor={orderDetails.vendor} />
                </div>
              )}

              {activeTab === "Payment Details" && orderDetails && (
                <div className="space-y-4 sm:space-y-6">
                  <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-4 sm:p-6">
                    <h3 className="text-lg sm:text-xl font-bold text-gray-800 mb-4 flex items-center space-x-2">
                      <span>💳</span>
                      <span>Payment Information</span>
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                      {[
                        { label: "Payment Mode", value: orderDetails.payment_mode, icon: <FaMoneyBill1Wave /> },
                        { label: "Payment Status", value: orderDetails.payment_status, icon: <MdPayments />, isStatus: true },
                        { label: "Transaction ID", value: orderDetails.transaction_id, icon: <FaIdBadge /> },
                      ].map((item, index) => (
                        <div key={index} className="bg-white rounded-xl p-3 sm:p-4 shadow-sm hover:shadow-md transition-shadow duration-200">
                          <div className="flex items-center space-x-3">
                            <div className="p-2 bg-gray-50 rounded-lg text-gray-500 flex-shrink-0">
                              {item.icon}
                            </div>
                            <div className="min-w-0">
                              <p className="text-xs text-gray-500 font-medium">{item.label}</p>
                              {item.isStatus ? (
                                <span className={`px-2 sm:px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(item.value)}`}>
                                  {item.value}
                                </span>
                              ) : (
                                <p className="text-sm font-semibold text-gray-800 break-words">{item.value}</p>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="flex justify-center sm:justify-end mt-4 sm:mt-6">
                      <button
                        onClick={() => setIsModalOpen(true)}
                        className="w-full sm:w-auto bg-gradient-to-r from-primaryRed to-orange-500 hover:from-orange-500 hover:to-primaryRed text-white px-4 sm:px-6 py-2 rounded-xl font-medium transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105"
                      >
                        💰 Refund Amount
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === "Refund Details" && orderDetails?.paymentData?.[0]?.refundData && (
                <div className="transform hover:scale-[1.01] transition-transform duration-200">
                  <RefundDetailsCard refund={orderDetails.paymentData[0].refundData} />
                </div>
              )}
            </div>
          </>
        ) : (
          <div className="p-12 text-center">
            <div className="text-6xl mb-4">🔍</div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2">No Order Found</h2>
            <p className="text-gray-600">Please enter a valid order ID to track your order</p>
          </div>
        )}
      </div>
      
      {isModalOpen && <RefundModal isOpen={isModalOpen} onClose={closeModal} />}
    </div>
  );
};

export default TrackOrderPage;
